import { createBrowserRouter, Outlet, useRouteError } from 'react-router-dom';
import { AlmanacRoutes } from '../../modules/Almanac/routes';
import { AprovaLiveRoutes } from '../../modules/AoVivo/routes';
import { AuthRoutes } from '../../modules/Auth/routes';
import { CommunitRoutes } from '../../modules/Community/routes';
import { CoursesRoutes } from '../../modules/Courses/routes';
import { DisciplinesRoutes } from '../../modules/Disciplines/routes';
import { EssayRoutes } from '../../modules/Essay/routes';
import { FavoritoRoutes } from '../../modules/Favorites/routes';
import { GamificationRoutes } from '../../modules/Gamification/Routes';
import { MyAreaRoutes } from '../../modules/MyArea/routes';
import { OnboardingRoutes } from '../../modules/Onboarding/routes';
import { PracticeRoutes } from '../../modules/Practice/routes';
import { ProfileRoutes } from '../../modules/Profile/routes';
import { RegionalsRoutes } from '../../modules/Regionals/routes';
import { SearchRoutes } from '../../modules/Search/routes';
import { SimuladaoRoutes, SimuladaoAreaLogadaRoutes } from '../../modules/Simuladao/routes';
import { Toast } from '../components/atoms/Toast';
import { AuthenticatedLayout } from '../components/templates/AuthenticatedLayout';
import ErrorPage from '../components/templates/ErrorPage';
import Template from '../templates/baseApp';
import SimuladaoTemplate from '../templates/simuladao';

import { PlanoAdaptativoRoutes } from '../../modules/PlanoAdaptativo/routes';
import { lazy } from 'react';
import RedirectRoutes from '../../modules/Redirect/routes';
import { MemoryRoutes } from '../../modules/Memory/routes';
import { enemGabaritoRoutes } from '../../modules/EnemGabarito/routes';
import { MemberGetMemberRoutes } from '../../modules/MemberGetMember/routes';
import { GeneralSimulationRoutes } from '../../modules/GeneralSimulation/routes';
import { IndicacaoRoutes } from '../../modules/Indicacao/routes';
import { ExerciseBankRoutes } from '../../modules/ExerciseBank/routes';
import {
  Enem365Routes,
  LegacyEnem365Redirect,
} from '../../modules/Enem365/routes';
import { FreemiumRoutes } from '../../modules/Freemium/routes';
import { ConexoesJubilutRoutes } from '../../modules/ConexoesJubilut/routes';
import { AreaEnemRoutes } from '../../modules/AreaEnem/routes';

const SimuladaoEnviaEmailMatriculaPage = lazy(() => import('../../modules/Simuladao/pages/UpdateMatriculationData'));
const SimuladaoAtualizaDadosMatriculaPage = lazy(() => import('../../modules/Simuladao/pages/UpdateMatriculationData/updateDataPage'));

function RootErrorBoundary() {
  const error = useRouteError() as Error;

  // Verifica se é erro de Chunk faltante para recarregar a página automaticamente
  const isChunkError = 
    error?.message?.includes('Loading chunk') || 
    error?.message?.includes('MimeType') ||
    error?.name === 'ChunkLoadError';

  if (isChunkError) {
    console.warn('🔄 Chunk desatualizado detectado pelo React Router. Recarregando...');
    window.location.reload();
    return null;
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Ops! Ocorreu um erro na rota.</h2>
      <pre>{error?.message || JSON.stringify(error)}</pre>
    </div>
  );
}

const Router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: [...MyAreaRoutes],
  },
  {
    path: '/',
    element: <Outlet />,
    ErrorBoundary: RootErrorBoundary,
    children: AuthRoutes,
  },
  {
    path: '/',
    element: <Outlet />,
    children: FreemiumRoutes,
  },
  {
    path: 'redacoes',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: EssayRoutes,
  },
  {
    path: 'favoritos',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: FavoritoRoutes,
  },
  {
    path: 'search',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: SearchRoutes,
  },
  {
    path: 'disciplinas',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: DisciplinesRoutes,
  },
  {
    path: 'cursos',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: CoursesRoutes,
  },
  {
    path: 'onboarding',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: OnboardingRoutes,
  },
  {
    path: 'simulados',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: PracticeRoutes,
  },
  {
    path: 'area-enem',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: AreaEnemRoutes,
  },
  {
    path: 'regionais',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: RegionalsRoutes,
  },
  {
    path: 'comunidade',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: CommunitRoutes,
  },
  {
    path: 'conexoes-jubilut',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: ConexoesJubilutRoutes,
  },
  {
    path: 'ao-vivo',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: AprovaLiveRoutes,
  },
  {
    path: 'perfil',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: ProfileRoutes,
  },
  {
    path: 'almanaque',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: AlmanacRoutes,
  },
  {
    path: 'simuladao',
    element: (
      <SimuladaoTemplate>
        <Toast />
        <Outlet />
      </SimuladaoTemplate>
    ),
    children: SimuladaoRoutes,
  },
  {
    path: 'simuladao/area-logada',
    element: <Outlet />,
    children: SimuladaoAreaLogadaRoutes,
  },
  {
    path: 'simulado-enem',
    element: <Outlet />,
    children: Enem365Routes,
  },
  {
    path: 'enem-365/*',
    element: <LegacyEnem365Redirect />,
  },
  {
    path: 'atualizacao-dados-simuladao',
    children: [
      {
        path: '/atualizacao-dados-simuladao',
        element: <SimuladaoEnviaEmailMatriculaPage />,
      },
      {
        path: '/atualizacao-dados-simuladao/:token',
        element: <SimuladaoAtualizaDadosMatriculaPage />,
      },
    ],
  },
  {
    path: 'game',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: GamificationRoutes,
  },
  {
    path: 'plano-adaptativo',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: PlanoAdaptativoRoutes,
  },
  {
    path: 'indicacao',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: MemberGetMemberRoutes,
  },
  {
    path: 'redireciona',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: RedirectRoutes,
  },
  {
    path: 'memory',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: MemoryRoutes,
  },
  {
    path: '/',
    element: <Outlet />,
    children: enemGabaritoRoutes,
  },
  {
    path: 'general-simulation',
    element: (
      <AuthenticatedLayout>
        <Outlet />
      </AuthenticatedLayout>
    ),
    children: GeneralSimulationRoutes,
  },
  {
    path: 'referidos',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: IndicacaoRoutes,
  },
  {
    path: 'banco-questoes',
    element: (
      <AuthenticatedLayout>
        <Template>
          <Outlet />
        </Template>
      </AuthenticatedLayout>
    ),
    children: ExerciseBankRoutes,
  },
  {
    path: '*',
    element: <ErrorPage />,
  },
]);

export default Router;
